import { Box, Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import { getCommonColumns } from 'csam/admin/components/CommonColumns';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState } from 'react';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';

interface FalseLoginData {
  id: number;
  user_id: number;
  email: string;
  sso_email: string;
  created_at: string;
}

interface FalseLoginDataResponse {
  success: boolean;
  data: FalseLoginData[];
}

const FalseLoginData = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const deleteMutation = useAuthenticatedMutation<string[], void>(['delete-false-data'], (ids) => ({
    url: `${fixed}admin/${locale}/false_login_data`,
    method: 'DELETE',
    data: { ids },
  }));

  const queryClient = useQueryClient();

  const handleDelete = (ids?: string[]) => {
    const idsToDelete = ids || selectedIds;
    deleteMutation.mutate(idsToDelete.map((id) => id.toString()) as unknown as void, {
      onSuccess: () => {
        toast.success(`Record Deleted Successfully`);
        queryClient.invalidateQueries({
          queryKey: ['FalseLogin', 'FalseLoginPageData', locale],
        });
      },
    });
  };

  const headers = [
    { label: 'Id', key: 'id' },
    { label: 'User Id', key: 'user_id' },
    { label: 'Username', key: 'email' },
    { label: 'Email (SSO)', key: 'sso_email' },
    { label: 'Created At (IST)', key: 'created_at' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      filterable: false,
      width: 40,
      renderCell: (params) => params.value,
    },

    {
      field: 'user_id',
      headerName: 'User ID',
      width: 80,
      renderCell: (params) => params.value,
    },
    {
      field: 'email',
      headerName: 'Username',
      width: 250,
      renderCell: (params) => params.value,
    },
    {
      field: 'sso_email',
      headerName: 'Email (SSO)',
      width: 250,
      renderCell: (params) => params.value,
    },
    ...getCommonColumns({
      handleDelete,
      showEdit: false,
      showYear: false,
      showCreatedAt: true,
      showDelete: true,
    }),
  ];

  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<FalseLoginDataResponse>(
    ['FalseLogin', 'FalseLoginPageData', locale],
    {
      url: `${fixed}admin/${locale || localeFromStorage}/false_login_data`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (isPending) return <AdminPageLoader />;
  if (error) return <div>Error: {error.message}</div>;
  if (data && !data.success) return <AdminInternalError />;

  const prepareCSVData = (
    data: FalseLoginData[],
  ): Array<{
    id: number;
    user_id: number;
    email: string;
    sso_email: string;
    created_at: string;
  }> => {
    return data.map((item) => ({
      id: item.id,
      user_id: item.user_id,
      email: item.email,
      sso_email: item.sso_email,
      created_at: item.created_at,
    }));
  };

  return (
    <AdminWrapper>
      <Box className="falseLoginDataAdmin">
        <AdminPageHeader
          title="False Login Data"
          locale={locale}
          handleDelete={handleDelete}
          setLocale={setLocale}
          showLocaleButtonGroup={false}
          enableBulkDelete={true}
        >
          <Box
            className="optionalHeaderData"
            sx={{ display: 'flex', justifyContent: 'end', alignItems: 'end', width: '27%' }}
          >
            <Button
              type="button"
              sx={{ color: '#fff', lineHeight: '26px' }}
              variant="contained"
              size="small"
              color="success"
              className="download-csv-button"
              title="Download CSV"
            >
              <CSVLink
                data={data?.data ? prepareCSVData(data.data) : []}
                headers={headers}
                filename="false_login_data.csv"
              >
                <CloudDownloadIcon sx={{ fontSize: '18px' }} /> CSV
              </CSVLink>
            </Button>
          </Box>
        </AdminPageHeader>
      </Box>
      <AdminDataGrid<FalseLoginData>
        data={!data ? null : { data: Array.isArray(data.data) ? data.data : [] }}
        columns={columns}
        setSelectedIds={setSelectedIds}
        showCheckbox={true}
      />
    </AdminWrapper>
  );
};

export default FalseLoginData;
